import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { AuthError, ErrorCodes } from '../lib/errors';
import type { User, Session } from '@supabase/supabase-js';
import { useSubscriptionStore } from '../stores/subscriptionStore';

// Helper function to get the correct redirect URL based on environment
const getRedirectUrl = () => {
  const isProd = window.location.hostname === 'www.divineintelligence.app';
  return isProd 
    ? 'https://www.divineintelligence.app/auth/callback'
    : `${window.location.origin}/auth/callback`;
};

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  showAuthModal: boolean;
  setUser: (user: User | null) => void;
  setSession: (session: Session | null) => void;
  setShowAuthModal: (show: boolean) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signUp: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  resendConfirmationEmail: (email: string) => Promise<void>;
  initializeAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  session: null,
  loading: true,
  showAuthModal: false,
  setUser: (user) => {
    set({ user });
    if (user) {
      useSubscriptionStore.getState().loadSubscription();
    }
  },
  setSession: (session) => {
    set({ session });
  },
  setShowAuthModal: (show) => set({ showAuthModal: show }),

  initializeAuth: async () => {
    try {
      // First try to get session from localStorage
      const storedSession = localStorage.getItem('divine-intelligence-auth-session');
      if (storedSession) {
        const session = JSON.parse(storedSession);
        set({ session, user: session.user });
      }

      // Then get the current session from Supabase
      const { data: { session } } = await supabase.auth.getSession();

      // Clear stored session if no active session exists
      if (!session && storedSession) {
        localStorage.removeItem('divine-intelligence-auth-session');
      }

      if (session) {
        set({ session, user: session.user });
        localStorage.setItem('divine-intelligence-auth-session', JSON.stringify(session));
      }

      set({ loading: false });
    } catch (error) {
      console.error('Error initializing auth:', error);
      set({ loading: false });
    }
  },

  signInWithGoogle: async () => {
    try {
      set({ loading: true });
      const redirectUrl = getRedirectUrl();
      console.log('Starting Google sign in with redirect URL:', redirectUrl);
      
      const { data: { session }, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      if (error) {
        console.error('Google sign in error:', error);
        throw new AuthError(
          'Failed to sign in with Google',
          ErrorCodes.AUTH_UNKNOWN_ERROR
        );
      }

      if (session) {
        set({ session, user: session.user, showAuthModal: false });
        localStorage.setItem('divine-intelligence-auth-session', JSON.stringify(session));
      }

    } catch (error) {
      console.error('Error signing in with Google:', error);
      if (error instanceof AuthError) {
        throw error;
      }
      throw new AuthError(
        'An unexpected error occurred while signing in with Google',
        ErrorCodes.AUTH_UNKNOWN_ERROR
      );
    } finally {
      set({ loading: false });
    }
  },

  signIn: async (email: string, password: string) => {
    try {
      set({ loading: true });
      const { data: { session }, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        if (error.message.includes('Email not confirmed')) {
          throw new AuthError(
            'Please confirm your email address before signing in',
            ErrorCodes.UserNotConfirmed
          );
        }
        throw new AuthError(
          'Invalid email or password',
          ErrorCodes.InvalidCredentials
        );
      }

      if (session) {
        set({ session, user: session.user, showAuthModal: false });
        localStorage.setItem('divine-intelligence-auth-session', JSON.stringify(session));
      }

    } catch (error) {
      console.error('Error signing in:', error);
      if (error instanceof AuthError) {
        throw error;
      }
      throw new AuthError(
        'An unexpected error occurred while signing in',
        ErrorCodes.AUTH_UNKNOWN_ERROR
      );
    } finally {
      set({ loading: false });
    }
  },

  signUp: async (email: string, password: string) => {
    try {
      set({ loading: true });
      console.log('Starting signup process...');
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });

      console.log('Signup response:', { data, error });

      if (error) {
        console.error('Signup error:', error);
        throw new AuthError(
          error.message,
          ErrorCodes.AUTH_UNKNOWN_ERROR
        );
      }

      if (data.user?.identities?.length === 0) {
        console.error('User identities empty');
        throw new AuthError(
          'An account with this email already exists',
          ErrorCodes.AUTH_EMAIL_IN_USE
        );
      }

      return data;
    } catch (error) {
      console.error('Error in signup process:', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  signOut: async () => {
    try {
      console.log('Signing out...');
      
      // Sign out of Supabase first and wait for it to complete
      await supabase.auth.signOut();
      
      // Clear all auth state
      set({ user: null, session: null });
      
      // Clear all stored data
      localStorage.removeItem('divine-intelligence-auth-session');
      localStorage.removeItem('divine-intelligence-bible-state');
      
      // Redirect to home page
      window.location.replace('/');
    } catch (error) {
      console.error('Error signing out:', error);
      // Even if there's an error, clear state and redirect
      set({ user: null, session: null });
      localStorage.removeItem('divine-intelligence-auth-session');
      localStorage.removeItem('divine-intelligence-bible-state');
      window.location.replace('/');
    }
  },

  resetPassword: async (email: string) => {
    try {
      set({ loading: true });
      const { error } = await supabase.auth.resetPasswordForEmail(email);
      if (error) throw error;
    } catch (error) {
      console.error('Error resetting password:', error);
      throw new AuthError(
        'An unexpected error occurred while resetting password',
        ErrorCodes.AUTH_UNKNOWN_ERROR
      );
    } finally {
      set({ loading: false });
    }
  },

  resendConfirmationEmail: async (email: string) => {
    try {
      set({ loading: true });
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
      });
      if (error) throw error;
    } catch (error) {
      console.error('Error resending confirmation email:', error);
      throw new AuthError(
        'An unexpected error occurred while resending confirmation email',
        ErrorCodes.AUTH_UNKNOWN_ERROR
      );
    } finally {
      set({ loading: false });
    }
  },
}));
